import { AgGridReact } from "ag-grid-react"
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { ColDef, ColGroupDef, RowNode, RowClassParams, RowStyle, ValueFormatterParams } from "ag-grid-community"
import { CorrectionEntry, MatchStatus, PositionDiff, ReconciliationResult } from "../../../../../api/reconciliation"
import { RowOverride } from "../reconciliation.utils"
import { StatusFilter } from "../useReconciliation"
import {
  GridContainer,
  PRICE_GAP_BREACH_CELL_STYLE,
  PRICE_GAP_BREACH_PRICE_CELL_STYLE,
} from "../Reconciliation.styled"
import {
  correctionKey,
  describePriceGapControl,
  formatCorrectionHistory,
  formatPct,
  getStatusLabel,
  STATUS_COLORS,
  numberFormatter,
  decimalFormatter,
  localMvFormatter,
  fxFormatter,
  formatAssetType,
  isFeeAmountEditable,
  isPriceGapBreach,
  isRowEditable,
  matchCellStyle,
  orderDiffIndices,
} from "../reconciliation.utils"

interface ReconciliationGridProps {
  result: ReconciliationResult
  displayDiffs: PositionDiff[]
  statusFilter: StatusFilter
  edits: Map<string, RowOverride>
  correctionHistory: Map<string, CorrectionEntry[]>
  onCellEdit: (
    identifier: string,
    field: "price" | "quantity" | "market_value" | "comment",
    value: unknown,
  ) => void
}

// Price / quantity edits are allowed on Scient-side rows except merged fee
// lines (see isRowEditable). When the baseline triple is incomplete (e.g.
// bonds with no unit price, cash with no qty), `applyOverride` falls back to
// the baseline MV — the override is still recorded so the operator can
// capture intent and an explanation in the comment.

const ReconciliationGrid: React.FC<ReconciliationGridProps> = ({
  result,
  displayDiffs,
  statusFilter,
  edits,
  correctionHistory,
  onCellEdit,
}) => {
  const gridRef = useRef<AgGridReact>(null)

  // Hold the latest edits in a ref so cellClassRules / getRowStyle callbacks
  // read live state without forcing AG Grid's columnDefs to be reallocated on
  // every keystroke (which triggers a full column-tree reinit).
  const editsRef = useRef(edits)
  useEffect(() => {
    editsRef.current = edits
  }, [edits])

  // Same ref pattern for the correction audit log (#1629): tooltip getters
  // read live history without reallocating columnDefs when it refetches.
  const correctionHistoryRef = useRef(correctionHistory)
  useEffect(() => {
    correctionHistoryRef.current = correctionHistory
  }, [correctionHistory])

  // Tooltip on the editable Scient cells: the correction chain behind the
  // number — who changed it, from what to what, when, and why.
  const correctionTooltipGetter = useCallback(
    (field: "price" | "quantity" | "market_value") =>
      (params: { data?: PositionDiff }): string | null => {
        const data = params.data
        if (!data) return null
        const entries = correctionHistoryRef.current.get(
          correctionKey(data.identifier, field),
        )
        return entries?.length ? formatCorrectionHistory(entries) : null
      },
    [],
  )

  // Row order reads the server's rows for size and status, so an edit that
  // moves a market value never moves the row the operator is typing in; only
  // a breach the edit opened or closed moves its row (orderDiffIndices).
  const orderedIndices = useMemo(
    () => orderDiffIndices(result.diffs, displayDiffs),
    [result.diffs, displayDiffs],
  )
  const sortedRows = useMemo(
    () => orderedIndices.map(i => displayDiffs[i]).filter((d): d is PositionDiff => d != null),
    [displayDiffs, orderedIndices],
  )

  // Notify AG Grid when the external filter changes. The breaches filter
  // also opens every group: its point is to bring the breached rows up out
  // of the collapsed groups, not to hide them behind a closed one.
  useEffect(() => {
    const api = gridRef.current?.api
    api?.onFilterChanged()
    if (statusFilter === "price_gap_breaches") api?.expandAll()
  }, [statusFilter])

  // groupDefaultExpanded only applies to newly created group nodes. A new
  // file upload remounts the grid (result is reset to null first), but
  // re-comparing with the grid still mounted (same file, or a source-type
  // change) keeps the asset-type group nodes and whatever expansion state
  // the operator left. Collapse explicitly so every compare starts from the
  // overview (Lad's round-3 point 1).
  useEffect(() => {
    gridRef.current?.api?.collapseAll()
  }, [result])

  // AG Grid's immutable pipeline only refreshes cells whose own value changed
  // — so CACEIS-side cells (price_b, market_value_b) keep their amber
  // matchCellStyle even when the Scient edit brought the *_diff to 0, because
  // price_b's value didn't change. Force-refresh just the edited rows so
  // cellStyle re-evaluates against the new diff fields. Targeted by row node
  // (not the whole grid) and safe now that getRowId is unique — the earlier
  // classList crash was caused by duplicate row IDs, not by refreshCells.
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api) return
    const rowNodes: RowNode[] = []
    api.forEachNode((node: RowNode) => {
      const d = node.data as PositionDiff | undefined
      if (d && edits.has(d.identifier)) rowNodes.push(node)
    })
    if (rowNodes.length > 0) {
      api.refreshCells({ rowNodes, force: true, suppressFlash: true })
    }
  }, [edits])

  // Use the position index from result.diffs (attached upstream as __row_index)
  // as the row ID. It's guaranteed unique — the composite
  // match_status|asset_type|identifier key collides in real data for accrual
  // and dividend rows that share the same descriptor, and AG Grid v27 crashes
  // inside RowCtrl.updateRowIndexes when getRowId returns duplicates.
  const getRowId = useCallback(
    (params: { data?: PositionDiff & { __row_index?: number } }) => {
      const idx = params.data?.__row_index
      return idx != null ? String(idx) : ""
    },
    [],
  )

  const sourceA = result.source_a_name
  const sourceB = result.source_b_name

  // The price-gap control spelled out on the CACEIS price cell (#1750):
  // the quotes, the unit prices in fund currency the gap is read on, the
  // gap, the gap value and its share of the NAV, with the thresholds the
  // server applied. Nothing on a row outside the control.
  const thresholds = result.controls?.thresholds ?? null
  const navReference = result.total_market_value_b
  const priceGapTooltipGetter = useCallback(
    (params: { data?: PositionDiff }): string | null =>
      params.data && thresholds
        ? describePriceGapControl(params.data, thresholds, navReference)
        : null,
    [thresholds, navReference],
  )

  const priceGapCellStyle = useCallback((params: { data?: PositionDiff }) => {
    const data = params.data
    if (data && isPriceGapBreach(data)) return PRICE_GAP_BREACH_PRICE_CELL_STYLE
    return matchCellStyle("price_diff")(params)
  }, [])

  const columnDefs: (ColDef | ColGroupDef)[] = useMemo(
    () => [
      {
        field: "asset_type",
        headerName: "Asset Type",
        rowGroup: true,
        hide: true,
        valueFormatter: (params: ValueFormatterParams) => formatAssetType(params.value),
      },
      {
        headerName: "Position",
        children: [
      {
        field: "match_status",
        headerName: "Status",
        pinned: "left" as const,
        width: 110,
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value
            ? getStatusLabel(params.value as MatchStatus, sourceA, sourceB)
            : params.value
        },
        cellStyle: (params: any) => {
          const color = STATUS_COLORS[params.value as MatchStatus]
          return color ? { color, fontWeight: 600 } : undefined
        },
      },
      {
        field: "identifier",
        headerName: "Identifier",
        pinned: "left" as const,
        width: 160,
      },
      {
        field: "description",
        headerName: "Description",
        pinned: "left" as const,
        width: 200,
        tooltipField: "description",
      },
      {
        field: "comment",
        headerName: "Comment",
        pinned: "left" as const,
        width: 170,
        tooltipField: "comment",
        editable: true,
        cellEditor: "agTextCellEditor",
        cellClassRules: {
          "edited-cell": (params: any) => {
            const data = params.data as PositionDiff | undefined
            if (!data) return false
            const o = editsRef.current.get(data.identifier)
            return o?.comment != null && o.comment !== ""
          },
        },
      },
        ],
      },
      {
        headerName: "Quantity",
        children: [
          {
            field: "quantity_a",
            headerName: `${sourceA} Qty`,
            flex: 1,
            minWidth: 90,
            valueFormatter: numberFormatter,
            type: "numericColumn",
            aggFunc: "sum",
            cellStyle: matchCellStyle("quantity_diff"),
            tooltipValueGetter: correctionTooltipGetter("quantity"),
            editable: (params: any) => isRowEditable(params.data as PositionDiff),
            cellClassRules: {
              "edited-cell": (params: any) => {
                const data = params.data as PositionDiff | undefined
                if (!data) return false
                return editsRef.current.get(data.identifier)?.override_quantity !== undefined
              },
            },
          },
          {
            field: "quantity_b",
            headerName: `${sourceB} Qty`,
            flex: 1,
            minWidth: 90,
            valueFormatter: numberFormatter,
            type: "numericColumn",
            aggFunc: "sum",
            cellStyle: matchCellStyle("quantity_diff"),
          },
          {
            field: "quantity_diff",
            headerName: "Qty Diff",
            flex: 1,
            minWidth: 90,
            valueFormatter: numberFormatter,
            type: "numericColumn",
            aggFunc: "sum",
            cellStyle: matchCellStyle("quantity_diff"),
          },
        ],
      },
      {
        headerName: "Price",
        children: [
          {
            field: "price_a",
            headerName: `${sourceA} Price`,
            flex: 1,
            minWidth: 90,
            valueFormatter: decimalFormatter,
            type: "numericColumn",
            // A price-gap breach reads on the two prices that disagree
            // (CONTEXT.md, "Breach"): red on a red tint, over the amber
            // "any difference" colour; the row keeps its status stripe.
            cellStyle: priceGapCellStyle,
            tooltipValueGetter: correctionTooltipGetter("price"),
            editable: (params: any) => isRowEditable(params.data as PositionDiff),
            cellClassRules: {
              "edited-cell": (params: any) => {
                const data = params.data as PositionDiff | undefined
                if (!data) return false
                return editsRef.current.get(data.identifier)?.override_price !== undefined
              },
            },
          },
          {
            field: "price_b",
            headerName: `${sourceB} Price`,
            flex: 1,
            minWidth: 90,
            valueFormatter: decimalFormatter,
            type: "numericColumn",
            cellStyle: priceGapCellStyle,
            // The price-gap control spelled out where the two prices meet;
            // the Scient cell's tooltip carries the correction history.
            tooltipValueGetter: priceGapTooltipGetter,
          },
        ],
      },
      {
        headerName: "Trade ccy MV",
        children: [
              {
                field: "market_value_local_a",
                headerName: `${sourceA} MV`,
                flex: 1,
                minWidth: 110,
                valueFormatter: localMvFormatter,
                type: "numericColumn",
                cellStyle: matchCellStyle("market_value_local_diff"),
              },
              {
                field: "market_value_local_b",
                headerName: `${sourceB} MV`,
                flex: 1,
                minWidth: 110,
                valueFormatter: localMvFormatter,
                type: "numericColumn",
                cellStyle: matchCellStyle("market_value_local_diff"),
              },
              {
                field: "market_value_local_diff",
                headerName: `Diff (${sourceA} − ${sourceB})`,
                flex: 1,
                minWidth: 110,
                valueFormatter: localMvFormatter,
                type: "numericColumn",
                cellStyle: matchCellStyle("market_value_local_diff"),
              },
        ],
      },
      {
        headerName: "Fund ccy MV",
        children: [
              {
                field: "market_value_a",
                headerName: `${sourceA} MV`,
                flex: 1,
                minWidth: 100,
                valueFormatter: numberFormatter,
                type: "numericColumn",
                aggFunc: "sum",
                cellStyle: matchCellStyle("market_value_diff"),
                tooltipValueGetter: correctionTooltipGetter("market_value"),
                // Fee rows are corrected by amount — direct market value
                // (#1630); every other row's MV stays computed, never typed.
                editable: (params: any) => isFeeAmountEditable(params.data as PositionDiff),
                cellClassRules: {
                  "edited-cell": (params: any) => {
                    const data = params.data as PositionDiff | undefined
                    if (!data) return false
                    return editsRef.current.get(data.identifier)?.override_market_value !== undefined
                  },
                },
              },
              {
                field: "market_value_b",
                headerName: `${sourceB} MV`,
                flex: 1,
                minWidth: 100,
                valueFormatter: numberFormatter,
                type: "numericColumn",
                aggFunc: "sum",
                cellStyle: matchCellStyle("market_value_diff"),
              },
              {
                field: "market_value_diff",
                headerName: `Diff (${sourceA} − ${sourceB})`,
                flex: 1,
                minWidth: 100,
                valueFormatter: numberFormatter,
                type: "numericColumn",
                aggFunc: "sum",
                cellStyle: matchCellStyle("market_value_diff"),
              },
        ],
      },
      {
        headerName: "FX rate",
        children: [
              {
                field: "implied_fx_a",
                headerName: sourceA,
                flex: 1,
                minWidth: 90,
                valueFormatter: fxFormatter,
                type: "numericColumn",
                hide: true,
              },
              {
                field: "implied_fx_b",
                headerName: sourceB,
                flex: 1,
                minWidth: 90,
                valueFormatter: fxFormatter,
                type: "numericColumn",
                hide: true,
              },
        ],
      },
    ],
    [sourceA, sourceB, correctionTooltipGetter, priceGapTooltipGetter, priceGapCellStyle],
  )

  // Under the Breaches filter the grid is the price-gap table: the position
  // columns, the two quotes (the Scient one still editable, a correction
  // clears the breach live), the two unit prices in fund currency the gap
  // is read on (#1755, read-only: they follow the valuation) and the
  // control's figures — gap, gap value and its share of the NAV. Every
  // other filter keeps the full column set.
  const breachColumnDefs: (ColDef | ColGroupDef)[] = useMemo(() => {
    const groups = columnDefs as ColGroupDef[]
    const leaves = groups.flatMap(def => (def.children as ColDef[] | undefined) ?? [def as ColDef])
    const pick = (field: string): ColDef => {
      const col = leaves.find(def => def.field === field)
      if (!col) throw new Error(`breach columns: no column ${field}`)
      return col
    }
    const position = groups.find(def => def.headerName === "Position")
    const unitPriceCol = (field: "unit_price_a" | "unit_price_b", source: string): ColDef => ({
      field,
      headerName: `${source} (fund ccy)`,
      flex: 1,
      minWidth: 100,
      valueFormatter: decimalFormatter,
      type: "numericColumn",
      tooltipValueGetter: priceGapTooltipGetter,
    })
    const navSharePct = (data: PositionDiff | undefined): number | null =>
      data && navReference && data.price_gap_value != null
        ? (data.price_gap_value / Math.abs(navReference)) * 100
        : null
    return [
      pick("asset_type"),
      ...(position ? [position] : []),
      {
        headerName: "Price gap",
        children: [
          pick("price_a"),
          pick("price_b"),
          unitPriceCol("unit_price_a", sourceA),
          unitPriceCol("unit_price_b", sourceB),
          {
            field: "price_gap_pct",
            headerName: "Gap",
            flex: 1,
            minWidth: 90,
            type: "numericColumn",
            valueFormatter: (params: ValueFormatterParams) => formatPct(params.value),
            cellStyle: PRICE_GAP_BREACH_CELL_STYLE,
            tooltipValueGetter: priceGapTooltipGetter,
          },
          {
            field: "price_gap_value",
            headerName: "Gap value",
            flex: 1,
            minWidth: 110,
            type: "numericColumn",
            valueFormatter: numberFormatter,
            tooltipValueGetter: priceGapTooltipGetter,
          },
          {
            colId: "price_gap_nav_share",
            headerName: "% NAV",
            flex: 1,
            minWidth: 80,
            type: "numericColumn",
            valueGetter: (params: { data?: PositionDiff }) => navSharePct(params.data),
            valueFormatter: (params: ValueFormatterParams) => formatPct(params.value),
            tooltipValueGetter: priceGapTooltipGetter,
          },
        ],
      },
    ]
  }, [columnDefs, navReference, priceGapTooltipGetter, sourceA, sourceB])

  const autoGroupColumnDef: ColDef = useMemo(
    () => ({
      headerName: "Asset Type",
      minWidth: 200,
      pinned: "left" as const,
      cellRendererParams: {
        suppressCount: false,
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.node?.group) {
          return formatAssetType(params.node.key ?? null)
        }
        return params.value
      },
    }),
    [],
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
    }),
    [],
  )

  const getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    if (params.node?.group) return undefined

    const data = params.data as PositionDiff | undefined
    if (!data) return undefined

    // Edited rows take precedence over the match stripes: amber stripe +
    // subtle background tint so the operator can see at a glance which rows
    // they've touched.
    if (editsRef.current.has(data.identifier)) {
      return {
        borderLeft: "4px solid #D9822B",
        backgroundColor: "rgba(255, 200, 80, 0.08)",
      } as RowStyle
    }

    // Directional break colors: red = we hold something they don't (Scient
    // Only), orange = they hold something we don't (Caceis Only). The colors
    // encode which book to investigate.
    if (data.match_status === "source_a_only") {
      return { borderLeft: "4px solid #CD4246" } as RowStyle
    }
    if (data.match_status === "source_b_only") {
      return { borderLeft: "4px solid #EC9A3C" } as RowStyle
    }

    if (data.match_status === "paired") {
      return { borderLeft: "4px solid #2D72D2" } as RowStyle
    }

    return undefined
  }

  /**
   * External filter: filter rows by match status when a tag is clicked
   */
  const isExternalFilterPresent = useCallback(() => {
    return statusFilter !== "all"
  }, [statusFilter])

  const doesExternalFilterPass = useCallback(
    (node: RowNode) => {
      const data = node.data as PositionDiff | undefined
      if (!data) return false
      if (statusFilter === "price_gap_breaches") return isPriceGapBreach(data)
      return data.match_status === statusFilter
    },
    [statusFilter],
  )

  const statusBarConfig = useMemo(
    () => ({
      statusPanels: [{ statusPanel: "agTotalRowCountComponent", align: "left" as const }],
    }),
    [],
  )

  return (
    <GridContainer className="ag-theme-balham-dark">
      <AgGridReact
        ref={gridRef}
        rowData={sortedRows}
        columnDefs={statusFilter === "price_gap_breaches" ? breachColumnDefs : columnDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        groupDefaultExpanded={0}
        getRowId={getRowId}
        getRowStyle={getRowStyle}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
        headerHeight={32}
        rowHeight={28}
        enableCellTextSelection
        ensureDomOrder
        animateRows={false}
        tooltipShowDelay={300}
        statusBar={statusBarConfig}
        suppressAggFuncInHeader
        readOnlyEdit
        onCellEditRequest={(event: any) => {
          const data = event.data as PositionDiff
          const field = event.colDef.field
          if (field === "price_a") onCellEdit(data.identifier, "price", event.newValue)
          else if (field === "quantity_a") onCellEdit(data.identifier, "quantity", event.newValue)
          else if (field === "market_value_a") onCellEdit(data.identifier, "market_value", event.newValue)
          else if (field === "comment") onCellEdit(data.identifier, "comment", event.newValue)
        }}
      />
    </GridContainer>
  )
}

export default ReconciliationGrid
